import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, List, Select, Spin } from 'antd';
import { FctEstado } from '../../fct/react';
import {
  anadirAcceso, buscarEmpleados, leerAccesos, mensajeDeError, quitarAcceso, type EmpleadoDeAcceso,
} from '../../api/reportesApi';

/** Lo que espera el buscador tras la última tecla, para no consultar en cada una. */
const ESPERA_BUSQUEDA_MS = 300;

const etiquetaEmpleado = (e: EmpleadoDeAcceso) => `${e.nombre} (${e.email})`;

/**
 * Los accesos nominales de un informe (`FC_REPORTE_BI_ACCESO`, #411): quién lo abre sin tener acceso general. Añadir y quitar se
 * aplican al momento, sin esperar al Guardar del formulario. Un empleado de baja sigue en la lista, para poder quitarlo.
 */
const AccesosInforme: React.FC<{ idInforme: number }> = ({ idInforme }) => {
  const [accesos, setAccesos] = useState<EmpleadoDeAcceso[] | null>(null);
  const [hallados, setHallados] = useState<EmpleadoDeAcceso[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const espera = useRef<ReturnType<typeof setTimeout>>();
  const ultimaBusqueda = useRef(0);

  useEffect(() => {
    let vivo = true;
    leerAccesos(idInforme)
      .then((leidos) => { if (vivo) setAccesos(leidos); })
      .catch((e) => { if (vivo) setError(mensajeDeError(e, 'No se han podido cargar los accesos del informe.')); });
    return () => { vivo = false; clearTimeout(espera.current); };
  }, [idInforme]);

  // Solo cuenta la respuesta de la última búsqueda: una anterior puede llegar después.
  const buscar = (texto: string) => {
    clearTimeout(espera.current);
    const busqueda = ++ultimaBusqueda.current;
    if (texto.trim().length < 2) {
      setHallados([]);
      setBuscando(false);
      return;
    }
    setBuscando(true);
    espera.current = setTimeout(() => {
      buscarEmpleados(texto)
        .then((encontrados) => { if (busqueda === ultimaBusqueda.current) setHallados(encontrados); })
        .catch((e) => { if (busqueda === ultimaBusqueda.current) setError(mensajeDeError(e, 'No se han podido buscar empleados.')); })
        .finally(() => { if (busqueda === ultimaBusqueda.current) setBuscando(false); });
    }, ESPERA_BUSQUEDA_MS);
  };

  // Cada cambio devuelve la lista como queda.
  const cambiar = (accion: Promise<EmpleadoDeAcceso[]>) => {
    setError(null);
    accion
      .then(setAccesos)
      .catch((e) => setError(mensajeDeError(e, 'No se ha podido cambiar el acceso. Inténtalo de nuevo.')));
  };

  const yaConAcceso = new Set((accesos ?? []).map((a) => a.idEmpleado));

  return (
    <section aria-label="Accesos nominales" className="reportes-form__accesos">
      <h3>Accesos nominales</h3>
      <p className="reportes-form__ayuda">Empleados que abren este informe sin tener acceso general. Añadir y quitar se aplica al momento.</p>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      <Select<number>
        showSearch
        aria-label="Añadir empleado"
        placeholder="Busca por nombre o email para añadir"
        style={{ width: '100%', marginBottom: 8 }}
        filterOption={false}
        value={null as unknown as number}
        loading={buscando}
        notFoundContent={buscando ? <Spin size="small" /> : 'Escribe al menos dos letras'}
        onSearch={buscar}
        onSelect={(idEmpleado) => {
          setHallados([]);
          cambiar(anadirAcceso(idInforme, idEmpleado));
        }}
        options={hallados.filter((e) => !yaConAcceso.has(e.idEmpleado)).map((e) => ({ value: e.idEmpleado, label: etiquetaEmpleado(e) }))}
      />
      {accesos === null && !error && <Spin size="small" />}
      {accesos && (
        <List
          size="small"
          bordered
          dataSource={accesos}
          locale={{ emptyText: 'Ningún empleado tiene acceso nominal a este informe.' }}
          renderItem={(e) => (
            <List.Item
              actions={[
                <Button key="quitar" size="small" aria-label={`Quitar a ${e.nombre}`} onClick={() => cambiar(quitarAcceso(idInforme, e.idEmpleado))}>
                  Quitar
                </Button>,
              ]}
            >
              <span>{etiquetaEmpleado(e)}</span>
              {!e.activo && (
                <span style={{ marginLeft: 8 }}>
                  <FctEstado personalizado={{ etiqueta: 'Baja', familia: 'neutro', icono: 'cancelado' }} />
                </span>
              )}
            </List.Item>
          )}
        />
      )}
    </section>
  );
};

export default AccesosInforme;
